import './App.css'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import LoginPage from './pages/LoginPage'
import UsersPage from './pages/UsersPage'
import SecurityUsersPage from './pages/SecurityUsersPage'
import Sidebar from './components/Sidebar'
import BuildingsPage from './pages/BuildingsPage'
import BuildingDetailPage from './pages/BuildingDetailPage'
import InvitationsPage from './pages/InvitationsPage'
import PrivacyPolicy from './components/PrivacyPolicy'
import EventsPage from './pages/EventsPage'
import EventsPageByBuilding from './pages/EventsPageByBuilding'
import ErrorsPage from './pages/ErrorsPage'
import CommunicationsPage from './pages/CommunicationsPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import UserInvitationsPage from './pages/UserInvitationPage'
import UserEventsFormPage from './pages/UserEventsFormPage'
import UserInvitationPageSuccess from './pages/UserInvitationPageSuccess'
import GuestsPage from './pages/GuestsPage'
import UserEventsPage from './pages/UserEventsPage'
import RequestAccessFormPage from './pages/RequestAccessFormPage'
import NotificationsPage from './pages/NotificationsPage'
import ServicesListPage from './pages/ServicesListPage'
import ManualEventForm from './components/events/ManualEventForm'

import BlacklistedPage from './pages/BlacklistedPage'
import ConstructionsPage from './pages/ConstructionsPage'
import ConstructionDetailPage from './pages/ConstructionDetailPage'
import AmenitiesPage from './pages/AmenitiesPage'
import CreateAmenityPage from './pages/CreateAmenityPage'
import ReservationsPage from './pages/ReservationsPage'
import ReservationsReportPage from './pages/ReservationsReportPage'
import MonthlyReportsPage from './pages/MonthlyReportsPage'

import { useGetUserInfoWithLoading } from './hooks/useGetUserInfoWithLoading'
import { useIsSecurityUserWithLoading } from './hooks/useIsSecurityUserWithLoading'
import ShoppingPage from './pages/ShoppingPage'
import CreateCommunicationPage from './pages/CreateCommunicationPage'
import CreateInvitationPage from './pages/CreateInvitationPage'
import CreateUserPage from './pages/CreateUserPage'
import CreateBlacklistedPage from './pages/CreateBlacklistedPage'
import CreateBuildingPage from './pages/CreateBuildingPage'
import CreateConstructionPage from './pages/CreateConstructionPage'
import CreateGuestPage from './pages/CreateGuestPage'
import cookies from './utils/cookies'
import CreateApartmentPage from './pages/CreateApartmentPage'
import CreateDevicePage from './pages/CreateDevicePage'
import CreateBroadcastGroupPage from './pages/CreateBroadcastGroupPage'
import { paths } from './utils/paths'
import CreateShoppingPage from './pages/CreateShoppingPage'
import { NotificationProvider } from './hooks/notifications/notificationContext'
import DoorbellsPage from './pages/DoorbellsPage'
import ProtectedRoute from './components/common/ProtectedRoute'

function App() {
  const { user, loading: userLoading } = useGetUserInfoWithLoading()
  const { isSecurityUser, loading: isSecurityUserLoading } =
    useIsSecurityUserWithLoading()

  if (userLoading || isSecurityUserLoading) {
    return null
  }

  let loggedUser
  const getLoggedUser = () => {
    try {
      const user = cookies.get('user')
      return JSON.parse(user)
    } catch (e) {
      return null
    }
  }
  loggedUser = getLoggedUser()

  return (
    <div className="App">
      <Router>
        <NotificationProvider>
          <Switch>
            <Route path={paths.REGISTER}>
              <RegisterPage />
            </Route>
            <Route path={paths.FORGOT_PASSWORD}>
              <ForgotPasswordPage />
            </Route>
            <Route path={paths.LOGIN}>
              <LoginPage />
            </Route>
            <Route path={paths.PRIVACY_POLICY}>
              <PrivacyPolicy />
            </Route>
            <ProtectedRoute path={paths.USERS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <UsersPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.SECURITY_USERS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <SecurityUsersPage />
              </div>
            </ProtectedRoute>

            <ProtectedRoute path={paths.NEW_USER}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateUserPage
                  building={isSecurityUser ? user.building : null}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.BUILDING_DETAIL}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <BuildingDetailPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_APARTMENT}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateApartmentPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_DEVICE}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateDevicePage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_BROADCAST_GROUP}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateBroadcastGroupPage
                  buildingId={loggedUser?.building?.id}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.BUILDINGS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <BuildingsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_BUILDING}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateBuildingPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.EVENTS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <EventsPageByBuilding />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.ERRORS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ErrorsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.COMMUNICATIONS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CommunicationsPage
                  building={isSecurityUser ? user.building : null}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_COMMUNICATION}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateCommunicationPage
                  building={isSecurityUser ? user.building : null}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.INVITATIONS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <InvitationsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_INVITATION}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateInvitationPage
                  building={isSecurityUser ? user.building : null}
                />
              </div>
            </ProtectedRoute>
            <Route path={paths.USER_INVITATIONS}>
              <UserInvitationsPage />
            </Route>
            <Route path={paths.USER_INVITATION_SUCCESS}>
              <UserInvitationPageSuccess />
            </Route>
            <ProtectedRoute path={paths.GUESTS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <GuestsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_GUEST}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateGuestPage building={loggedUser?.building} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.SERVICES}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ServicesListPage />
              </div>
            </ProtectedRoute>
            <Route path={paths.NEW_USER_EVENTS}>
              <UserEventsFormPage />
            </Route>
            <Route path={paths.USER_EVENTS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <UserEventsPage />
              </div>
            </Route>
            <ProtectedRoute path={paths.REQUEST_ACCESS}>
              <RequestAccessFormPage />
            </ProtectedRoute>
            <ProtectedRoute path={paths.BUILDING_EVENTS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <EventsPageByBuilding />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.USER_EVENTS_FOR_BUILDING}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <UserEventsPage building={true} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NOTIFICATIONS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <NotificationsPage building={true} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.SHOPPING}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ShoppingPage building={true} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_SHOPPING}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateShoppingPage building={user?.building} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.MANUAL_ACCESS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ManualEventForm isModal={false} building={user?.building} />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.BLACKLISTED}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <BlacklistedPage
                  building={user?.building}
                  isSecurityUser={isSecurityUser}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_BLACKLISTED}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateBlacklistedPage
                  building={isSecurityUser ? user.building : null}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.CONSTRUCTION_DETAIL}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ConstructionDetailPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.CONSTRUCTIONS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ConstructionsPage
                  building={user?.building}
                  isSecurityUser={isSecurityUser}
                />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_CONSTRUCTION}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateConstructionPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.AMENITIES}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <AmenitiesPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.NEW_AMENITY}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <CreateAmenityPage />
              </div>
            </ProtectedRoute>
            {/* Order matters — the more specific child paths come first so
                react-router-dom v5 matches them before the /reservas prefix. */}
            <ProtectedRoute path={paths.RESERVATIONS_REPORT}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ReservationsReportPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.MONTHLY_REPORTS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <MonthlyReportsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.RESERVATIONS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <ReservationsPage />
              </div>
            </ProtectedRoute>
            <ProtectedRoute path={paths.DOORBELLS}>
              <div className="root">
                <Sidebar user={loggedUser} />
                <DoorbellsPage />
              </div>
            </ProtectedRoute>
            <Route path="/">
              <LoginPage />
            </Route>
          </Switch>
        </NotificationProvider>
      </Router>
    </div>
  )
}

export default App
